import React from 'react';
import { Table, Tag, Button, Form, Select, InputNumber, Typography, Tooltip } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
const ScenarioRunsViewer = ({ runs, loading, totalRuns, currentPage, pageSize, onRunSelect, onConfigChange, onFiltersChange, initialFilters, }) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const handleTableChange = (pagination, filters, sorter) => {
        onConfigChange({ page: pagination.current, pageSize: pagination.pageSize }, filters, sorter);
    };
    const handleFilterSubmit = (values) => {
        onFiltersChange({
            version: values.version ? parseInt(values.version, 10) : undefined,
            sort: values.sort,
        });
    };
    const handleClearFilters = () => {
        form.resetFields();
        onFiltersChange({});
    };
    const columns = [
        {
            title: React.createElement(Trans, { i18nKey: "runId", defaults: "Run ID" }),
            dataIndex: '_id',
            key: '_id',
            render: (text) => {
                if (!text) {
                    return null;
                }
                const isLong = text.length > 10;
                if (!isLong) {
                    return (React.createElement(Typography.Text, { copyable: { text } },
                        React.createElement("code", null, text)));
                }
                const truncatedText = `${text.slice(0, 6)}...${text.slice(-4)}`;
                const tooltipTitle = (React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
                    React.createElement("span", { style: { marginRight: '8px' } }, text),
                    React.createElement(Typography.Text, { copyable: {
                            text: text,
                            icon: [
                                React.createElement(CopyOutlined, { key: "copy-icon", style: { color: 'white' } }),
                                React.createElement(CheckOutlined, { key: "copied-icon", style: { color: 'white' } }),
                            ],
                        } })));
                return (React.createElement(Tooltip, { title: tooltipTitle },
                    React.createElement("code", null, truncatedText)));
            },
        },
        {
            title: React.createElement(Trans, { i18nKey: "status", defaults: "Status" }),
            dataIndex: 'success',
            key: 'success',
            render: (success) => (React.createElement(Tag, { color: success ? 'green' : 'red' }, success ? React.createElement(Trans, { i18nKey: "success", defaults: "Success" }) : React.createElement(Trans, { i18nKey: "failed", defaults: "Failed" }))),
            filters: [
                { text: t('success', 'Success'), value: true },
                { text: t('failed', 'Failed'), value: false },
            ],
            filterMultiple: false,
        },
        {
            title: React.createElement(Trans, { i18nKey: "conditionResult", defaults: "Condition Result" }),
            key: 'conditionResult',
            render: (_, record) => {
                if (record.conditionsResult === undefined) {
                    return t('notAvailable', 'N/A');
                }
                return (React.createElement(Tag, { color: record.conditionsResult ? 'green' : 'red' }, String(record.conditionsResult)));
            },
        },
        {
            title: React.createElement(Trans, { i18nKey: "started", defaults: "Started" }),
            dataIndex: 'started',
            key: 'started',
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: React.createElement(Trans, { i18nKey: "versionTitle", defaults: "Version" }),
            dataIndex: 'version',
            key: 'version',
        },
        {
            title: React.createElement(Trans, { i18nKey: "executionTimeMs", defaults: "Execution Time (ms)" }),
            dataIndex: 'executionTime',
            key: 'executionTime',
        },
        {
            title: React.createElement(Trans, { i18nKey: "actionsTitle", defaults: "Actions" }),
            key: 'actions',
            render: (_, record) => (React.createElement(Button, { type: "primary", onClick: () => onRunSelect(record._id) },
                React.createElement(Trans, { i18nKey: "viewDetails", defaults: "View Details" }))),
        },
    ];
    return (React.createElement("div", null,
        React.createElement(Form, { form: form, layout: "inline", onFinish: handleFilterSubmit, initialValues: initialFilters, style: { marginBottom: '24px' } },
            React.createElement(Form.Item, { name: "version", label: React.createElement(Trans, { i18nKey: "versionTitle", defaults: "Version" }) },
                React.createElement(InputNumber, { min: 1, style: { width: 120 }, placeholder: t('versionFilterPlaceholder', 'e.g. 1') })),
            React.createElement(Form.Item, { name: "sort", label: React.createElement(Trans, { i18nKey: "sortBy", defaults: "Sort By" }) },
                React.createElement(Select, { style: { width: 220 }, placeholder: React.createElement(Trans, { i18nKey: "sortDefaultPlaceholder", defaults: "Default" }), allowClear: true },
                    React.createElement(Select.Option, { value: "started_desc" },
                        React.createElement(Trans, { i18nKey: "sortStartedNewest", defaults: "Start Time (Newest First)" })),
                    React.createElement(Select.Option, { value: "started_asc" },
                        React.createElement(Trans, { i18nKey: "sortStartedOldest", defaults: "Start Time (Oldest First)" })),
                    React.createElement(Select.Option, { value: "executionTime_desc" },
                        React.createElement(Trans, { i18nKey: "sortExecutionSlowest", defaults: "Execution Time (Slowest)" })),
                    React.createElement(Select.Option, { value: "executionTime_asc" },
                        React.createElement(Trans, { i18nKey: "sortExecutionFastest", defaults: "Execution Time (Fastest)" })))),
            React.createElement(Form.Item, null,
                React.createElement(Button, { type: "primary", htmlType: "submit" },
                    React.createElement(Trans, { i18nKey: "filter", defaults: "Filter" }))),
            React.createElement(Form.Item, null,
                React.createElement(Button, { onClick: handleClearFilters },
                    React.createElement(Trans, { i18nKey: "clear", defaults: "Clear" })))),
        React.createElement(Table, { dataSource: runs, columns: columns, loading: loading, rowKey: "_id", pagination: {
                current: currentPage,
                pageSize: pageSize,
                total: totalRuns,
            }, onChange: handleTableChange })));
};
export default ScenarioRunsViewer;
